import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Landmark, Upload, Search, Send, Bot, User, FileSpreadsheet, Loader2, Sparkles, ListTree, Scale, ChevronRight, FileDown, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { formatCurrency, formatPercent } from '../lib/format';
import { exportCEPdf } from '../lib/exportPdf';
import { useAuth } from '../context/AuthContext';
import { MarkdownMessage } from '../components/MarkdownMessage';

const SUGGESTIONS = [
  "Riassumi l'andamento economico del 2025",
  'Quali sono le prime 5 voci di costo?',
  "Com'è composto il costo del venduto?",
  'Qual è il punto di pareggio (BEP) e come lo miglioro?',
];

export default function FierSpa({ embedded = false, initialYear = null, onBack = null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'control'; // sezione Analisi CG aperta al Controllo di gestione
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [accQuery, setAccQuery] = useState('');
  const fileRef = useRef(null);
  const pdfRef = useRef(null);

  // partitario (drill-down) + quadratura
  const [partitario, setPartitario] = useState(null);
  const [quadratura, setQuadratura] = useState(null);
  const [partQuery, setPartQuery] = useState('');
  const [movConto, setMovConto] = useState(null);
  const [movData, setMovData] = useState(null);
  const [movLoading, setMovLoading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  // chat
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  const loadList = async () => {
    const list = await api.getFierBilanci();
    setYears(list);
    if (list.length && year == null) setYear(list[0].year);
    if (!list.length) { setData(null); setLoading(false); }
    return list;
  };

  useEffect(() => { loadList().catch(() => setLoading(false)); }, []);

  useEffect(() => {
    if (year == null) return;
    setLoading(true);
    api.getFierBilancio(year)
      .then((d) => setData(d))
      .catch(() => toast.error('Errore nel caricamento del bilancio'))
      .finally(() => setLoading(false));
    setMessages([]);
    // Partitario (mastrini) + Quadratura
    setPartitario(null); setQuadratura(null); setMovConto(null); setMovData(null);
    api.getFierPartitario(year).then(setPartitario).catch(() => setPartitario(null));
    api.getFierQuadratura(year).then(setQuadratura).catch(() => setQuadratura(null));
  }, [year]);

  const openMov = async (conto) => {
    setMovConto(conto); setMovData(null); setMovLoading(true);
    try {
      const d = await api.getFierMovimenti(year, conto);
      setMovData(d);
    } catch (err) {
      toast.error('Impossibile caricare i movimenti del conto');
      setMovConto(null);
    } finally {
      setMovLoading(false);
    }
  };

  const onUploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.uploadFierPartitario(fd, year || undefined);
      toast.success(`Partitario ${res.year} caricato (${res.n_accounts} conti, ${res.n_movimenti} movimenti)`);
      const [p, q] = await Promise.all([
        api.getFierPartitario(res.year).catch(() => null),
        api.getFierQuadratura(res.year).catch(() => null),
      ]);
      setPartitario(p); setQuadratura(q);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore durante il caricamento del partitario');
    } finally {
      setUploadingPdf(false);
      if (pdfRef.current) pdfRef.current.value = '';
    }
  };

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.uploadFierBilancio(fd);
      toast.success(`Bilancio ${res.year} caricato (${res.n_ce_rows} voci CE, ${res.n_accounts} conti)`);
      const list = await loadList();
      if (list.find((x) => x.year === res.year)) setYear(res.year);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending || year == null) return;
    const next = [...messages, { role: 'user', content: msg }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const res = await api.fierChat({ year, message: msg, history: next.slice(0, -1) });
      setMessages([...next, { role: 'assistant', content: res.answer }]);
    } catch (err) {
      setMessages([...next, { role: 'assistant', content: 'Si è verificato un errore nel generare la risposta. Riprova.' }]);
      toast.error('Errore assistente AI');
    } finally {
      setSending(false);
    }
  };

  const s = data?.summary || {};
  const filteredAccounts = useMemo(() => {
    const q = accQuery.trim().toLowerCase();
    const list = data?.accounts || [];
    if (!q) return list;
    return list.filter((a) => (a.conto || '').toLowerCase().includes(q) || (a.descrizione || '').toLowerCase().includes(q));
  }, [data, accQuery]);

  const filteredPart = useMemo(() => {
    const q = partQuery.trim().toLowerCase();
    const list = partitario?.conti || [];
    if (!q) return list;
    return list.filter((a) => (a.conto || '').toLowerCase().includes(q) || (a.descrizione || '').toLowerCase().includes(q));
  }, [partitario, partQuery]);

  return (
    <div className="space-y-6" data-testid="fier-spa-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          {embedded && onBack && (
            <Button variant="ghost" size="icon" className="mt-0.5 shrink-0" onClick={onBack} data-testid="fier-back-btn"><ArrowLeft className="h-5 w-5" /></Button>
          )}
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Landmark className="h-5 w-5 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{embedded ? `Bilancio annuale${year ? ` · ${year}` : ''}` : 'Fier S.p.A.'}</h1>
            <p className="text-sm text-muted-foreground">{embedded ? 'Bilancio riclassificato ufficiale · conti completi e assistente AI' : 'Bilanci consolidati riclassificati per anno · versione digitale, conti completi e assistente AI'}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {years.length > 0 && (
            <Select value={year != null ? String(year) : undefined} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[110px] md:w-[130px]" data-testid="fier-year-select"><SelectValue placeholder="Anno" /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y.year} value={String(y.year)}>{y.year}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onUpload} data-testid="fier-upload-input" />
              <Button variant="outline" className="flex-1 md:flex-none" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="fier-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" /> : <Upload className="h-4 w-4 mr-2 shrink-0" />}
                <span className="truncate">{uploading ? 'Caricamento…' : 'Carica bilancio'}</span>
              </Button>
              <input ref={pdfRef} type="file" accept=".pdf" className="hidden" onChange={onUploadPdf} data-testid="fier-upload-pdf-input" />
              <Button variant="outline" className="flex-1 md:flex-none" onClick={() => pdfRef.current?.click()} disabled={uploadingPdf} data-testid="fier-upload-pdf-btn">
                {uploadingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" /> : <ListTree className="h-4 w-4 mr-2 shrink-0" />}
                <span className="truncate">{uploadingPdf ? 'Caricamento…' : 'Carica partitario'}</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-muted-foreground">Caricamento…</Card>
      ) : !data ? (
        <Card className="p-10 text-center">
          <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-foreground font-medium mb-1">Nessun bilancio caricato</p>
          <p className="text-sm text-muted-foreground mb-4">Carica il primo bilancio riclassificato in formato Excel (.xlsx).</p>
          {isAdmin && <Button onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4 mr-2" />Carica bilancio</Button>}
        </Card>
      ) : (
        <>
          {/* KPI sintesi */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Ricavi', value: s.ricavi, tone: 'text-foreground' },
              { label: 'EBITDA / MOL', value: s.ebitda, tone: (s.ebitda || 0) >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]' },
              { label: 'EBIT', value: s.ebit, tone: (s.ebit || 0) >= 0 ? 'text-foreground' : 'text-[hsl(var(--danger))]' },
              { label: 'Utile di bilancio', value: s.utile_bilancio, tone: (s.utile_bilancio || 0) >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]' },
            ].map((k) => (
              <Card key={k.label} className="p-4" data-testid={`fier-kpi-${k.label}`}>
                <div className="text-xs text-muted-foreground">{k.label}</div>
                <div className={`text-xl sm:text-2xl font-semibold tabular-nums mt-1 truncate ${k.tone}`}>{formatCurrency(k.value || 0)}</div>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="ce" className="w-full">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
              <TabsTrigger value="ce" data-testid="fier-tab-ce">Conto Economico</TabsTrigger>
              <TabsTrigger value="conti" data-testid="fier-tab-conti">Tutti i conti ({data.n_accounts})</TabsTrigger>
              {partitario && <TabsTrigger value="partitario" data-testid="fier-tab-partitario"><ListTree className="h-3.5 w-3.5 mr-1.5" />Partitario ({partitario.n_accounts})</TabsTrigger>}
              {quadratura?.available && <TabsTrigger value="quadratura" data-testid="fier-tab-quadratura"><Scale className="h-3.5 w-3.5 mr-1.5" />Quadratura</TabsTrigger>}
              <TabsTrigger value="ai" data-testid="fier-tab-ai"><Sparkles className="h-3.5 w-3.5 mr-1.5" />Assistente AI</TabsTrigger>
            </TabsList>

            {/* Conto Economico riclassificato */}
            <TabsContent value="ce" className="mt-4">
              <Card className="p-0 overflow-hidden">
                <div className="px-5 py-3 border-b flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold">Conto Economico riclassificato · {data.year}</h3>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="h-8" data-testid="fier-ce-export-btn"
                      onClick={() => exportCEPdf({ company: data.company || 'Fier S.p.A.', title: 'Conto Economico riclassificato', period: `Esercizio ${data.year}`, rows: data.ce_rows || [], pctFraction: false })}>
                      <FileDown className="h-3.5 w-3.5 mr-1.5" />Esporta PDF
                    </Button>
                    <Badge variant="outline" className="text-[10px] hidden sm:inline-flex">{data.company}</Badge>
                  </div>
                </div>
                <div className="max-h-[62vh] overflow-y-auto overflow-x-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card z-10">
                      <TableRow>
                        <TableHead className="w-[52%]">Voce</TableHead>
                        <TableHead className="text-right hidden sm:table-cell">% ricavi</TableHead>
                        <TableHead className="text-right">Valore</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data.ce_rows || []).map((r, i) => {
                        if (r.kind === 'section') {
                          return (
                            <TableRow key={i} className="bg-muted/40">
                              <TableCell colSpan={3} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground py-2">{r.label}</TableCell>
                            </TableRow>
                          );
                        }
                        const isTotal = r.kind === 'total';
                        return (
                          <TableRow key={i} className={isTotal ? (r.strong ? 'bg-primary/5' : 'bg-muted/20') : ''} data-testid={`fier-ce-row-${i}`}>
                            <TableCell className={`${isTotal ? 'font-semibold' : ''} ${r.kind === 'account' ? 'pl-6' : ''}`}>
                              {r.code && <span className="text-[11px] text-muted-foreground mr-2 tabular-nums">{r.code}</span>}
                              {r.label}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground hidden sm:table-cell">{r.pct != null ? formatPercent(r.pct, 1, false) : ''}</TableCell>
                            <TableCell className={`text-right tabular-nums ${isTotal ? 'font-semibold' : ''} ${(r.value || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{r.value != null ? formatCurrency(r.value) : ''}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            {/* Tutti i conti */}
            <TabsContent value="conti" className="mt-4">
              <Card className="p-0 overflow-hidden">
                <div className="px-5 py-3 border-b flex flex-col sm:flex-row sm:items-center gap-3">
                  <h3 className="text-sm font-semibold sm:mr-auto">Elenco conti (mastrini) · {data.n_accounts}</h3>
                  <div className="relative w-full sm:w-64">
                    <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input value={accQuery} onChange={(e) => setAccQuery(e.target.value)} placeholder="Cerca conto o descrizione…" className="pl-8 h-9" data-testid="fier-account-search" />
                  </div>
                </div>
                <div className="max-h-[62vh] overflow-y-auto overflow-x-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card z-10">
                      <TableRow>
                        <TableHead className="w-[110px] md:w-[130px]">Conto</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead className="text-right hidden md:table-cell">Saldo apert.</TableHead>
                        <TableHead className="text-right hidden md:table-cell">Dare</TableHead>
                        <TableHead className="text-right hidden md:table-cell">Avere</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAccounts.map((a, i) => (
                        <TableRow key={i} data-testid={`fier-account-row-${i}`}>
                          <TableCell className="tabular-nums text-xs text-muted-foreground align-top">{a.conto}</TableCell>
                          <TableCell className="text-sm max-w-[150px] whitespace-normal break-words md:max-w-none">{a.descrizione}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground hidden md:table-cell">{formatCurrency(a.saldo_apertura)}</TableCell>
                          <TableCell className="text-right tabular-nums hidden md:table-cell">{formatCurrency(a.dare)}</TableCell>
                          <TableCell className="text-right tabular-nums hidden md:table-cell">{formatCurrency(a.avere)}</TableCell>
                          <TableCell className={`text-right tabular-nums font-medium ${(a.saldo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(a.saldo)}</TableCell>
                        </TableRow>
                      ))}
                      {filteredAccounts.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nessun conto trovato</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </TabsContent>

            {/* Partitario (drill-down mastrini) */}
            {partitario && (
              <TabsContent value="partitario" className="mt-4">
                <Card className="p-0 overflow-hidden">
                  <div className="px-5 py-3 border-b flex flex-col sm:flex-row sm:items-center gap-3">
                    <h3 className="text-sm font-semibold sm:mr-auto">Partitario · {partitario.n_accounts} conti · {partitario.n_movimenti} movimenti</h3>
                    <div className="relative w-full sm:w-64">
                      <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                      <Input value={partQuery} onChange={(e) => setPartQuery(e.target.value)} placeholder="Cerca conto o descrizione…" className="pl-8 h-9" data-testid="fier-part-search" />
                    </div>
                  </div>
                  <div className="max-h-[62vh] overflow-y-auto overflow-x-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-card z-10">
                        <TableRow>
                          <TableHead className="w-[110px] md:w-[130px]">Conto</TableHead>
                          <TableHead>Descrizione</TableHead>
                          <TableHead className="text-right hidden md:table-cell">Movimenti</TableHead>
                          <TableHead className="text-right">Saldo</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPart.map((a, i) => (
                          <TableRow key={i} className="cursor-pointer hover:bg-accent/60 transition-colors" onClick={() => openMov(a.conto)} data-testid={`fier-part-row-${i}`}>
                            <TableCell className="tabular-nums text-xs text-muted-foreground align-top">{a.conto}</TableCell>
                            <TableCell className="text-sm max-w-[160px] whitespace-normal break-words md:max-w-none">{a.descrizione}</TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground hidden md:table-cell">{a.n_movimenti}</TableCell>
                            <TableCell className={`text-right tabular-nums font-medium ${(a.saldo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(a.saldo)}</TableCell>
                            <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                          </TableRow>
                        ))}
                        {filteredPart.length === 0 && (
                          <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nessun conto trovato</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </TabsContent>
            )}

            {/* Quadratura CE riclassificato ↔ conti */}
            {quadratura?.available && (
              <TabsContent value="quadratura" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Conti partitario', value: quadratura.n_conti_partitario },
                    { label: 'Conti nel CE', value: quadratura.n_conti_ce },
                    { label: 'Non riclassificati', value: quadratura.n_non_riclassificati, warn: quadratura.n_non_riclassificati > 0 },
                    { label: 'Δ Ricavi (part. − CE)', value: formatCurrency(quadratura.diff_ricavi), warn: Math.abs(quadratura.diff_ricavi || 0) > 1 },
                  ].map((k) => (
                    <Card key={k.label} className="p-4" data-testid={`fier-quad-kpi-${k.label}`}>
                      <div className="text-xs text-muted-foreground">{k.label}</div>
                      <div className={`text-xl sm:text-2xl font-semibold tabular-nums mt-1 truncate ${k.warn ? 'text-[hsl(var(--warning))]' : 'text-foreground'}`}>{k.value}</div>
                    </Card>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="p-4">
                    <h3 className="text-sm font-semibold mb-3">Totali dal partitario</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Ricavi (saldi a credito)</span><span className="tabular-nums font-medium">{formatCurrency(quadratura.tot_ricavi_partitario)}</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Costi (saldi a debito)</span><span className="tabular-nums font-medium">{formatCurrency(quadratura.tot_costi_partitario)}</span></div>
                      <div className="flex items-center justify-between border-t pt-2"><span className="text-muted-foreground">Ricavi da CE riclassificato</span><span className="tabular-nums font-medium">{formatCurrency(quadratura.ce_ricavi)}</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Differenza ricavi</span><span className={`tabular-nums font-semibold ${Math.abs(quadratura.diff_ricavi || 0) > 1 ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--success))]'}`}>{formatCurrency(quadratura.diff_ricavi)}</span></div>
                    </div>
                  </Card>
                  <Card className="p-0 overflow-hidden">
                    <div className="px-4 py-3 border-b"><h3 className="text-sm font-semibold">Conti non riclassificati nel CE ({quadratura.n_non_riclassificati})</h3></div>
                    <div className="max-h-[42vh] overflow-y-auto overflow-x-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-card z-10">
                          <TableRow>
                            <TableHead className="w-[120px]">Conto</TableHead>
                            <TableHead>Descrizione</TableHead>
                            <TableHead className="text-right">Saldo</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(quadratura.non_riclassificati || []).map((a, i) => (
                            <TableRow key={i} className="cursor-pointer hover:bg-accent/60" onClick={() => openMov(a.conto)} data-testid={`fier-quad-nr-${i}`}>
                              <TableCell className="tabular-nums text-xs text-muted-foreground">{a.conto}</TableCell>
                              <TableCell className="text-sm max-w-[150px] whitespace-normal break-words md:max-w-none">{a.descrizione}</TableCell>
                              <TableCell className={`text-right tabular-nums font-medium ${(a.saldo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(a.saldo)}</TableCell>
                            </TableRow>
                          ))}
                          {(quadratura.non_riclassificati || []).length === 0 && (
                            <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">Tutti i conti sono riclassificati ✓</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>
                </div>
              </TabsContent>
            )}

            {/* Assistente AI */}
            <TabsContent value="ai" className="mt-4">
              <Card className="flex flex-col h-[62vh]" data-testid="fier-ai-panel">
                <div className="px-5 py-3 border-b flex items-center gap-2">
                  <Bot className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Assistente AI · dialoga con il bilancio {data.year}</h3>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 && (
                    <div className="text-center text-muted-foreground py-6">
                      <Sparkles className="h-8 w-8 mx-auto mb-3 text-primary/60" />
                      <p className="text-sm mb-4">Fai una domanda sul bilancio {data.year}. Le risposte si basano solo sui dati caricati.</p>
                      <div className="flex flex-wrap gap-2 justify-center max-w-xl mx-auto">
                        {SUGGESTIONS.map((q) => (
                          <button key={q} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:bg-accent transition-colors" data-testid="fier-ai-suggestion">{q}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`fier-ai-msg-${m.role}`}>
                      {m.role === 'assistant' && <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-primary" /></div>}
                      <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground whitespace-pre-wrap' : 'bg-muted text-foreground'}`}>
                        {m.role === 'assistant' ? <MarkdownMessage content={m.content} /> : m.content}
                      </div>
                      {m.role === 'user' && <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0"><User className="h-4 w-4" /></div>}
                    </div>
                  ))}
                  {sending && (
                    <div className="flex gap-3 justify-start">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-primary" /></div>
                      <div className="bg-muted rounded-xl px-4 py-2.5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Sto analizzando…</div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="border-t p-3 flex items-center gap-2">
                  <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={`Scrivi una domanda sul bilancio ${data.year}…`} disabled={sending} data-testid="fier-ai-input" />
                  <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="fier-ai-send"><Send className="h-4 w-4" /></Button>
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Drill-down movimenti conto */}
          <Dialog open={!!movConto} onOpenChange={(o) => { if (!o) { setMovConto(null); setMovData(null); } }}>
            <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="fier-mov-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base">
                  <ListTree className="h-4 w-4 text-primary" />
                  {movData ? `${movData.conto} · ${movData.descrizione}` : `Conto ${movConto || ''}`}
                </DialogTitle>
              </DialogHeader>
              {movLoading ? (
                <div className="py-10 text-center text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Caricamento movimenti…</div>
              ) : movData ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Saldo apertura', value: movData.saldo_apertura },
                      { label: 'Totale dare', value: movData.tot_dare },
                      { label: 'Totale avere', value: movData.tot_avere },
                      { label: 'Saldo', value: movData.saldo, strong: true },
                    ].map((k) => (
                      <div key={k.label} className="rounded-lg border p-2.5">
                        <div className="text-[11px] text-muted-foreground">{k.label}</div>
                        <div className={`text-sm tabular-nums mt-0.5 ${k.strong ? 'font-semibold' : ''} ${(k.value || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(k.value || 0)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="max-h-[46vh] overflow-y-auto overflow-x-auto border rounded-lg mt-1">
                    <Table>
                      <TableHeader className="sticky top-0 bg-card z-10">
                        <TableRow>
                          <TableHead className="w-[90px]">Data</TableHead>
                          <TableHead>Descrizione</TableHead>
                          <TableHead className="text-right">Importo</TableHead>
                          <TableHead className="text-right hidden sm:table-cell">Saldo progr.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(movData.movements || []).map((m, i) => (
                          <TableRow key={i} data-testid={`fier-mov-row-${i}`}>
                            <TableCell className="tabular-nums text-xs text-muted-foreground">{m.data}</TableCell>
                            <TableCell className="text-sm">
                              {m.descr}
                              {m.controparte && <span className="block text-[11px] text-muted-foreground">{m.controparte}</span>}
                            </TableCell>
                            <TableCell className={`text-right tabular-nums ${(m.importo || 0) < 0 ? 'text-[hsl(var(--danger))]' : ''}`}>{formatCurrency(m.importo)}</TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground hidden sm:table-cell">{formatCurrency(m.saldo)}</TableCell>
                          </TableRow>
                        ))}
                        {(movData.movements || []).length === 0 && (
                          <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nessun movimento</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : null}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
